import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  Typography,
  Breadcrumbs,
  Link as MUILink,
  Paper,
  Stack,
  Alert,
  Skeleton,
  Chip,
  Button,
  LinearProgress,
  Tooltip,
  Switch,
  FormControlLabel,
  CircularProgress,
} from '@mui/material';
import {
  Home,
  MonitorHeart,
  CheckCircle,
  Warning,
  Error as ErrorIcon,
  Refresh,
  Circle,
} from '@mui/icons-material';
import { useQueryClient } from '@tanstack/react-query';
import { usePageTitle } from '@/hooks/usePageTitle';
import { useAuth } from '@/hooks/useAuth';
import { useAdminConfigs } from '@/hooks/queries/admin/useAdminConfigs';
import { useUpdateConfig } from '@/hooks/mutations/admin/useUpdateConfig';
import { queryKeys } from '@/lib/queryKeys';
import {
  useSystemHealth,
  type ComponentHealth,
  type ComponentStatus,
} from '@/hooks/queries/admin/useSystemHealth';

const PEOPLE_SYNC_TOGGLE_KEY = 'system_health.people_sync_worker';

/**
 * Admin-only switch that enables/disables the People Sync Worker.
 * When disabled, scheduled and manual syncs are skipped (fail-closed) and the
 * worker's absence no longer triggers the degraded-state banner.
 */
const PeopleSyncWorkerToggle: React.FC = () => {
  const queryClient = useQueryClient();
  const { data: rows = [], isLoading } = useAdminConfigs('system_health.');
  const updateConfig = useUpdateConfig();

  const row = rows.find((r) => r.config_key === PEOPLE_SYNC_TOGGLE_KEY);
  const value = row?.config_value as { enabled?: boolean } | undefined;
  const enabled = value?.enabled ?? true;

  const handleToggle = async (checked: boolean) => {
    await updateConfig.mutateAsync({
      configKey: PEOPLE_SYNC_TOGGLE_KEY,
      configValue: { enabled: checked },
    });
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: queryKeys.systemHealth.detail }),
      queryClient.invalidateQueries({ queryKey: queryKeys.systemHealth.status }),
    ]);
  };

  const busy = isLoading || updateConfig.isPending;

  return (
    <Paper variant="outlined" sx={{ p: 2.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2 }}>
        <Box>
          <Typography variant="subtitle1" fontWeight={600}>
            People Sync Worker
          </Typography>
          <Typography variant="body2" color="text.secondary">
            When disabled, scheduled and manual personnel syncs are skipped, the worker is excluded
            from health checks, and its absence will not trigger the system alert banner.
          </Typography>
        </Box>
        <FormControlLabel
          sx={{ flexShrink: 0, m: 0 }}
          control={
            busy ? (
              <CircularProgress size={20} sx={{ mx: 1.25 }} />
            ) : (
              <Switch
                checked={enabled}
                onChange={(e) => void handleToggle(e.target.checked)}
                disabled={busy}
              />
            )
          }
          label={enabled ? 'Enabled' : 'Disabled'}
          labelPlacement="start"
        />
      </Box>
    </Paper>
  );
};

const STATUS_CONFIG: Record<
  ComponentStatus,
  { label: string; color: 'success' | 'warning' | 'error'; icon: React.ReactElement; description: string }
> = {
  healthy: {
    label: 'Healthy',
    color: 'success',
    icon: <CheckCircle fontSize="small" />,
    description: 'Operating normally',
  },
  degraded: {
    label: 'Degraded',
    color: 'warning',
    icon: <Warning fontSize="small" />,
    description: 'Experiencing slower performance',
  },
  unavailable: {
    label: 'Unavailable',
    color: 'error',
    icon: <ErrorIcon fontSize="small" />,
    description: 'Service is not responding',
  },
};

const OVERALL_STATUS_MESSAGES: Record<ComponentStatus, string> = {
  healthy: 'All systems are operating normally. No issues detected.',
  degraded:
    'Some components are experiencing issues. The system is operational but may be slower than usual.',
  unavailable:
    'One or more critical components are unavailable. Some features may not work correctly.',
};

const formatResponseTime = (ms: number | null): string => {
  if (ms === null) return '—';
  if (ms < 1000) return `${ms}ms`;
  return `${(ms / 1000).toFixed(1)}s`;
};

const formatTimestamp = (iso: string): string => {
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return iso;
  }
};

const ComponentCard: React.FC<{ component: ComponentHealth }> = ({ component }) => {
  const config = STATUS_CONFIG[component.status];

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 2.5,
        display: 'flex',
        alignItems: 'center',
        gap: 2,
        borderLeftWidth: 4,
        borderLeftColor: `${config.color}.main`,
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          bgcolor: `${config.color}.50`,
          color: `${config.color}.main`,
          flexShrink: 0,
        }}
      >
        {config.icon}
      </Box>

      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            {component.name}
          </Typography>
          <Chip
            label={config.label}
            size="small"
            color={config.color}
            variant="outlined"
            icon={<Circle sx={{ fontSize: 8 }} />}
          />
        </Box>
        <Typography variant="body2" color="text.secondary">
          {component.description}
        </Typography>
        {component.details && (
          <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
            {component.details}
          </Typography>
        )}
      </Box>

      <Tooltip title="Response time">
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ fontFamily: 'monospace', flexShrink: 0 }}
        >
          {formatResponseTime(component.responseTimeMs)}
        </Typography>
      </Tooltip>
    </Paper>
  );
};

export const SystemHealthPage: React.FC = () => {
  const navigate = useNavigate();
  usePageTitle('System Health');
  const { isAdmin } = useAuth();
  const { data, isLoading, error, refetch, isFetching, dataUpdatedAt } = useSystemHealth();

  const overallConfig = data ? STATUS_CONFIG[data.overallStatus] : null;

  return (
    <Container maxWidth="md" sx={{ mt: 2, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <MUILink
            color="inherit"
            href="/"
            onClick={(event) => {
              event.preventDefault();
              navigate('/');
            }}
            sx={{ display: 'flex', alignItems: 'center' }}
          >
            <Home sx={{ mr: 0.5 }} fontSize="inherit" />
            Home
          </MUILink>
          <Typography color="text.primary">System Health</Typography>
        </Breadcrumbs>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <MonitorHeart color="primary" sx={{ fontSize: 36 }} />
            <Typography variant="h4" component="h1">
              System Health
            </Typography>
          </Box>
          <Button
            variant="outlined"
            startIcon={<Refresh />}
            onClick={() => void refetch()}
            disabled={isFetching}
            size="small"
          >
            {isFetching ? 'Checking…' : 'Refresh'}
          </Button>
        </Box>
      </Box>

      {isFetching && <LinearProgress sx={{ mb: 2, borderRadius: 1 }} />}

      {/* Loading state */}
      {isLoading && (
        <Stack spacing={2}>
          <Skeleton variant="rounded" height={100} />
          <Skeleton variant="rounded" height={80} />
          <Skeleton variant="rounded" height={80} />
          <Skeleton variant="rounded" height={80} />
        </Stack>
      )}

      {/* Error state */}
      {error && !isLoading && (
        <Alert severity="error" sx={{ mb: 2 }}>
          Unable to retrieve system health information. The monitoring service may be temporarily
          unavailable. Please try again in a few moments.
        </Alert>
      )}

      {/* Health data */}
      {data && overallConfig && (
        <Stack spacing={3}>
          {/* Overall status banner */}
          <Alert
            severity={overallConfig.color === 'success' ? 'success' : overallConfig.color === 'warning' ? 'warning' : 'error'}
            icon={overallConfig.icon}
            sx={{ '& .MuiAlert-message': { width: '100%' } }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Box>
                <Typography variant="subtitle1" fontWeight={600}>
                  System Status: {overallConfig.label}
                </Typography>
                <Typography variant="body2">
                  {OVERALL_STATUS_MESSAGES[data.overallStatus]}
                </Typography>
              </Box>
            </Box>
          </Alert>

          {/* Component cards */}
          <Box>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Components
            </Typography>
            <Stack spacing={1.5}>
              {data.components.map((component) => (
                <ComponentCard key={component.name} component={component} />
              ))}
            </Stack>
          </Box>

          {/* Admin controls */}
          {isAdmin && (
            <Box>
              <Typography variant="h6" sx={{ mb: 2 }}>
                Monitoring Controls
              </Typography>
              <PeopleSyncWorkerToggle />
            </Box>
          )}

          {/* Last checked */}
          <Typography variant="caption" color="text.secondary" align="center">
            Last checked: {dataUpdatedAt ? new Date(dataUpdatedAt).toLocaleString() : formatTimestamp(data.checkedAt)}
            {' · '}
            Auto-refreshes every 30 seconds
          </Typography>
        </Stack>
      )}
    </Container>
  );
};
